import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { generatePath, useLocation, useParams } from 'react-router-dom';
import { UserOutlined } from '@ant-design/icons';
import AvatarField from '@components/common/form/AvatarField';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import useListBase from '@hooks/useListBase';
import useTranslate from '@hooks/useTranslate';
import { BaseTable } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import { getFullImageUrl, orderNumber } from '@utils';

const messageIntl = defineMessages({
    objectName: 'Printer mapping',
});

function PrinterMappingListPage() {
    const translate = useTranslate();

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const params = useParams();

    const parentId = searchParams.get('deviceId');
    const deviceName = searchParams.get('deviceName');
    const printerName = searchParams.get('printerName');

    const { data, mixinFuncs, queryFilter, loading, pagination, changePagination, queryParams, serializeParams } =
        useListBase({
            apiConfig: apiConfig.printerMapping,
            options: {
                pageSize: DEFAULT_TABLE_ITEM_SIZE,
                objectName: translate.formatMessage(messageIntl.objectName),
            },
            params: {
                printerId: params.printerId,
            },
            override: (funcs) => {
                funcs.mappingData = (response) => {
                    if (response.result === true) {
                        return {
                            data: response.data.content,
                            total: response.data.totalElements,
                        };
                    }
                };
                funcs.getList = () => {
                    mixinFuncs.handleFetchList({
                        printerId: params.printerId,
                    });
                };
                funcs.getItemDetailLink = (dataRow) => {
                    const currentPath = location.pathname;
                    const searchParams = location.search;
                    return `${currentPath}/${dataRow.id}${searchParams}`;
                };
                funcs.getCreateLink = () => {
                    const currentPath = location.pathname;
                    const searchParams = location.search;
                    return `${currentPath}/create${searchParams}`;
                };
            },
        });

    const columns = [
        {
            title: '#',
            dataIndex: 'index',
            key: 'id',
            render: (text, record, index) => {
                return orderNumber(pagination, index);
            },
            width: 50,
        },
        {
            title: <FormattedMessage defaultMessage="#" />,
            dataIndex: 'image',
            key: 'image',
            align: 'center',
            width: 70,
            render: (image) => {
                return (
                    <div style={{ backgroundColor: '#171717' }}>
                        <AvatarField size="large" icon={<UserOutlined />} src={getFullImageUrl(image)} disablePreview={true} />
                    </div>
                );
            },
        },
        {
            title: <FormattedMessage defaultMessage="Process" />,
            dataIndex: ['printerGroup', 'name'],
            width: 300,
        },
        {
            title: translate.formatMessage(commonMessage.printerNumber),
            dataIndex: 'printerNumber',
            render: (data) => {
                return (
                    <>
                        {translate.formatMessage(commonMessage.printerNumber)} {data}
                    </>
                );
            },
        },
        mixinFuncs.renderActionColumn(
            {
                edit: mixinFuncs.hasPermission([apiConfig.printerMapping.update.baseURL]),
                delete: mixinFuncs.hasPermission([apiConfig.printerMapping.delete.baseURL]),
            },
            { width: '120px' },
        ),
    ].filter(Boolean);

    return (
        <PageWrapper
            routes={[
                {
                    breadcrumbName: translate.formatMessage(commonMessage.managerKitplay),
                    path: routes.listKitPlayDevicePage.path,
                },
                {
                    breadcrumbName: deviceName,
                    path: `${generatePath(routes.DeviceTab.path, {
                        customerId: params.customerId,
                    })}?deviceId=${parentId}&deviceName=${encodeURIComponent(deviceName)}`,
                },
                {
                    breadcrumbName: printerName,
                },
            ]}
        >
            <ListPage
                actionBar={mixinFuncs.renderActionBar()}
                baseTable={
                    <BaseTable
                        onChange={changePagination}
                        pagination={pagination}
                        loading={loading}
                        dataSource={data}
                        columns={columns}
                    />
                }
            />
        </PageWrapper>
    );
}
export default PrinterMappingListPage;
